/**
 * EmbedPane — renders one Tableau dashboard via the Embedding API v3.
 *
 * The library exposes a `<tableau-viz>` web component. Once we set its `src`
 * and `token` attributes, it handles auth and rendering. The Connected App
 * JWT is consumed once, at the initial load handshake — after that the
 * session inside Tableau's iframe persists on its own, so we fetch a token
 * only when a viz is (re)created. (An earlier version refreshed the token
 * every 8 minutes and rebuilt the viz each time, which reset the user's
 * filter and parameter selections every 8 minutes.)
 *
 * Tableau Embedding API v3 docs:
 *   https://help.tableau.com/current/api/embedding_api/en-us/index.html
 *
 * Common failure modes (good to know when debugging):
 *   - "Could not load viz" with no detail → JWT is malformed or the
 *     Connected App isn't enabled in Tableau Cloud admin.
 *   - "User does not exist in site" → JWT sub claim doesn't match a real
 *     Tableau user (or Connected App's user attribute mapping is misconfigured).
 *   - 404 on the iframe → content_url is wrong. Test by pasting
 *     `<server>/#/site/<site>/views/<content_url>` into your browser directly.
 *   - "Embedding domain not allowlisted" → Add localhost:5173 (or your prod
 *     domain) to the Connected App's "Domain Allowlist" in Tableau Cloud.
 */
import { useEffect, useRef, useState } from "react";
import { fetchTableauToken } from "../lib/tableau";

// Import side-effect: registers the <tableau-viz> custom element.
import "@tableau/embedding-api";

interface Props {
  dashboardId: string;
  contentUrl: string;
  tableauServer: string;
}

// React doesn't know about <tableau-viz> by default. Tell TypeScript it's
// a valid JSX intrinsic element with a permissive prop type.
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "tableau-viz": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          src?: string;
          token?: string;
          width?: string;
          height?: string;
          "hide-tabs"?: boolean | string;
          "toolbar"?: "top" | "bottom" | "hidden";
          "device"?: "default" | "desktop" | "tablet" | "phone";
        },
        HTMLElement
      >;
    }
  }
}

export function EmbedPane({ dashboardId, contentUrl, tableauServer }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Build the full embed URL.
  // Tableau Cloud URLs look like:
  //   https://prod-useast-a.online.tableau.com/t/<site>/views/<content_url>
  // (Note: /t/<site>/views/, NOT /#/site/<site>/views/ — the latter is the
  // browser UI URL, not the embed URL.)
  const embedUrl = `${tableauServer.replace(/\/$/, "")}/t/${getSiteFromContent(contentUrl, tableauServer)}/views/${contentUrl}`;

  useEffect(() => {
    let cancelled = false;

    async function loadToken() {
      try {
        setLoading(true);
        const resp = await fetchTableauToken(dashboardId);
        if (!cancelled) {
          setToken(resp.token);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadToken();

    // No refresh interval: the token is only needed for the initial embed
    // handshake (it expires at 9 minutes, but the established Tableau
    // session outlives it). Rebuilding the viz on a timer would reset the
    // user's filters — see the header comment.
    return () => {
      cancelled = true;
    };
  }, [dashboardId]);

  // Once we have a token, replace the container's children with the
  // <tableau-viz> element. We do this imperatively rather than via JSX
  // because the custom element needs to be created fresh each time the
  // token changes — re-rendering through React keeps the same DOM node
  // which Tableau's library doesn't like.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !token) return;

    container.innerHTML = "";

    // Wrapper we scale within; hides overflow while the viz renders at
    // its authored size. Starts at 100% height so automatic-sized
    // dashboards (which never get a fixed height from fit()) keep the
    // old fluid behavior; fit() overrides it for fixed layouts.
    const wrap = document.createElement("div");
    wrap.style.width = "100%";
    wrap.style.height = "100%";
    wrap.style.overflow = "hidden";

    const viz = document.createElement("tableau-viz");
    viz.setAttribute("src", embedUrl);
    viz.setAttribute("token", token);
    // toolbar="hidden" removes Tableau's built-in top toolbar (the
    // breadcrumb that looks like a fake browser URL bar, plus the native
    // Download / Share / Edit buttons). The portal's own top bar (with
    // the "Open in Tableau" link) is the only chrome above the viz.
    viz.setAttribute("toolbar", "hidden");
    // Show the workbook's sheet tabs only for the marketing performance
    // dashboard; every other embed keeps them hidden.
    if (dashboardId !== "marketing-performance") {
      viz.setAttribute("hide-tabs", "");
    }
    viz.style.display = "block";
    viz.style.width = "100%";
    viz.style.height = "100%";

    // Fixed-layout dashboards clip when the container is narrower than
    // the authored size (e.g. Windows 150% display scaling). Render at
    // authored size, then uniformly scale DOWN to fit. Scale is capped
    // at 1 — Tableau's canvas goes soft above 1x.
    let authored: { w: number; h: number } | null = null;

    const fit = () => {
      if (!authored) return;
      const scale = Math.min(wrap.clientWidth / authored.w, 1);
      viz.style.transformOrigin = "top left";
      viz.style.transform = `scale(${scale})`;
      // scale() doesn't shrink the layout box — collapse the gap.
      wrap.style.height = `${authored.h * scale}px`;
    };

    const onSizeKnown = (e: Event) => {
      // detail is the library's FirstVizSizeKnownEvent: the sheet size
      // lives at detail.vizSize.sheetSize, and chromeHeight is the extra
      // in-iframe chrome (sheet tabs when shown) above the sheet itself.
      const detail = (
        e as CustomEvent<{
          vizSize?: {
            sheetSize?: { maxSize?: { width: number; height: number } };
            chromeHeight?: number;
          };
        }>
      ).detail;
      const max = detail?.vizSize?.sheetSize?.maxSize;
      const chrome = detail?.vizSize?.chromeHeight ?? 0;
      if (max?.width && max?.height) {
        authored = { w: max.width, h: max.height + chrome };
        viz.style.width = `${authored.w}px`;
        viz.style.height = `${authored.h}px`;
        fit();
      }
      // No maxSize -> automatic-sized dashboard; leave fluid, no scaling.
    };

    viz.addEventListener("firstvizsizeknown", onSizeKnown);
    const ro = new ResizeObserver(fit);
    ro.observe(wrap);

    wrap.appendChild(viz);
    container.appendChild(wrap);

    return () => {
      ro.disconnect();
      viz.removeEventListener("firstvizsizeknown", onSizeKnown);
    };
  }, [token, embedUrl, dashboardId]);

  if (error) {
    return (
      <div className="p-6">
        <div
          className="rounded-md p-4 font-mono text-[13px]"
          style={{
            background: "var(--oxblood-bg)",
            color: "var(--oxblood)",
            border: "1px solid var(--oxblood)",
          }}
        >
          <div className="font-medium mb-2">Could not load dashboard</div>
          <div>{error}</div>
          <details className="mt-3 text-[11.5px] opacity-80">
            <summary className="cursor-pointer">Troubleshooting</summary>
            <ul className="mt-2 list-disc pl-5 space-y-1">
              <li>Check the Connected App is enabled in Tableau Cloud admin.</li>
              <li>
                Verify the embedding domain (<code>{window.location.origin}</code>) is
                in the Connected App&rsquo;s domain allowlist.
              </li>
              <li>Confirm the user identity is mapped to a real Tableau user.</li>
              <li>
                Test the URL directly:{" "}
                <code className="break-all">{embedUrl}</code>
              </li>
            </ul>
          </details>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full h-full relative"
      style={{ background: "var(--bg-elev)" }}
    >
      {loading && !token && (
        <div
          className="absolute inset-0 flex items-center justify-center text-[14px] font-serif italic tool-pulse"
          style={{ color: "var(--ink-3)" }}
        >
          Loading dashboard…
        </div>
      )}
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
}

/**
 * Pull the Tableau site name out of the full server URL.
 * `https://prod-useast-a.online.tableau.com` → throws (need site separately)
 * For now we just hardcode pulling from env or returning a sensible default.
 */
function getSiteFromContent(_contentUrl: string, _server: string): string {
  // We get the site from /api/tableau/dashboards response, passed via
  // props from the caller. But to avoid a prop drill, read from env.
  return import.meta.env.VITE_TABLEAU_SITE || "capitoldataanalytics";
}
